/**
 * Capture-scoped recorder for "what makes a frame" (see plans/render-graph-view.md).
 *
 * Records every `renderer.render(scene, camera)` call — including nested calls
 * issued by composers/postprocessing passes — together with the active render
 * target, for N animation frames. The result is a plain data structure the
 * rendergraph adapter turns into a {@link NodeGraph}.
 *
 * Principles (chrome-extension safe, see plan "Interception design"):
 * - **Capture-scoped**: nothing is wrapped while idle. `capture()` patches,
 *   records N frames, unpatches. Zero standing overhead.
 * - **Instance-level, duck-typed**: patches target renderer *instances* we pass
 *   in — never prototypes of the page's three.js, never Proxies. Works across
 *   realms and three versions.
 * - **Reversible & safe**: unpatch restores the original only if the method is
 *   still our wrapper — if the page replaced it mid-capture we leave it alone.
 * - **Weak references**: captured resources hold the live scene/camera/target
 *   via WeakRef plus primitive snapshots (same policy as IResourceService), so
 *   a retained capture never pins host-page objects. The live ref is what later
 *   phases use to preview a target or jump into a pass's camera.
 * - **Frame boundary via own heartbeat**: we run our own rAF loop and stamp
 *   calls with its counter — the page's scheduling (rAF, setAnimationLoop,
 *   multiple loops) is never touched. Calls before the first heartbeat tick
 *   belong to a partial frame and are discarded.
 */

import { isEditorInternalRender } from "../common/editorScene.js";

// #region Result data model

export type CapturedResourceKind = "renderer" | "scene" | "camera" | "renderTarget" | "canvas";

/** Primitive snapshots + a weak handle to a resource that took part in a frame. */
export interface CapturedResource {
    /** Stable within the capture; the three `uuid` when available. */
    readonly id: string;
    readonly kind: CapturedResourceKind;
    /** three `type` snapshot (e.g. "Scene", "PerspectiveCamera") where reachable. */
    readonly type?: string;
    readonly name?: string;
    /** Render targets: dimension snapshot for labeling. */
    readonly width?: number;
    readonly height?: number;
    /** The live object, or `undefined` after GC. Never store the result long-term. */
    deref(): object | undefined;
}

/** One `renderer.render(scene, camera)` call. Fields reference {@link CapturedResource} ids. */
export interface CapturedRenderCall {
    /** Global order across the capture — execution order within a frame. */
    readonly order: number;
    /** 0-based index into {@link RenderCaptureResult.frames}. */
    readonly frame: number;
    readonly renderer: string;
    readonly scene: string;
    readonly camera: string;
    /** Active output at call time: a render target, or the renderer's canvas. */
    readonly target: string;
    /** >0 → issued from within another render call (composer/pass internals). */
    readonly depth: number;
    /** "shadow" = three's shadow-map pass, drawn INSIDE render() via
     *  renderer.shadowMap.render() and therefore invisible to a render()-only
     *  capture. Absent for ordinary render calls. */
    readonly kind?: "shadow";
    /**
     * High-level tallies of the renderer's sorted draw list for this call —
     * item COUNTS per bucket, read right after the render returns (no per-item
     * capture). Absent when the list isn't reachable (backend/version, or the
     * page cleared it). See {@link readRenderListStats}.
     */
    readonly renderList?: RenderListStats;
}

/** Per-bucket object counts of a pass's sorted draw list (three's buckets). */
export interface RenderListStats {
    readonly opaque: number;
    readonly transmissive: number;
    readonly transparent: number;
    /** opaque + transmissive + transparent — the pass's draw-call count. */
    readonly total: number;
}

export interface CapturedFrame {
    readonly index: number;
    /** In execution order. May be empty if nothing rendered that frame. */
    readonly calls: readonly CapturedRenderCall[];
}

export interface RenderCaptureResult {
    readonly frames: readonly CapturedFrame[];
    readonly resources: ReadonlyMap<string, CapturedResource>;
    /** True when {@link RenderCapture.cancel} ended the capture early. */
    readonly cancelled: boolean;
}

// #region Options

export interface RenderCaptureOptions {
    /**
     * Number of RENDERED frames to record (idle heartbeat ticks — on-demand
     * pages that pause — don't count; the capture stays armed until the page
     * renders). Default 1.
     *
     * `"continuous"` turns the capture into a STANDING observer: the wraps stay
     * installed indefinitely and every rendered frame is reported via
     * {@link onFrame} (the `capture()` promise never resolves; end with
     * `cancel()`). This is the always-on "last frame" recorder — a
     * capture-scoped recorder cannot see frames that happened before it armed,
     * so on-demand pages need an observer from the moment a renderer is
     * discovered.
     */
    frames?: number | "continuous";
    /** Called once per RENDERED frame in continuous mode, with a single-frame result. */
    onFrame?: (frame: RenderCaptureResult) => void;
    /**
     * Heartbeat scheduler, fired once per animation frame. Defaults to
     * `requestAnimationFrame` (resolved lazily at capture time — never captured
     * at module scope). Injectable so tests drive frames deterministically.
     */
    scheduleFrame?: (callback: () => void) => void;
    /**
     * Transactions that run around each renderer.render call. The standing
     * observer owns the renderer wrapper, so integrations join here instead
     * of stacking another wrapper on the host renderer.
     */
    renderParticipants?: () => readonly RenderParticipant[];
}

export interface RenderParticipantContext {
    readonly renderer: object;
    readonly scene: unknown;
    readonly camera: unknown;
    readonly target: unknown;
    readonly depth: number;
}

/** Cleanup runs after the render; completed is false if rendering or setup threw. */
export type RenderParticipant = (context: RenderParticipantContext) => ((completed: boolean) => void) | void;

// #region Duck types

/** The minimal renderer shape we patch — duck-typed, never `instanceof`. */
interface RendererLike {
    render: (...args: unknown[]) => unknown;
    setRenderTarget?: (...args: unknown[]) => unknown;
    getRenderTarget?: () => unknown;
    domElement?: unknown;
}

function isRendererLike(value: unknown): value is RendererLike {
    return !!value && typeof value === "object"
        && typeof (value as RendererLike).render === "function";
}

// #region Render-list stats

/**
 * Best-effort per-bucket COUNTS of a renderer's sorted draw list for one
 * pass — the high-level overview (no per-item capture). Duck-typed across
 * backends and three versions, guarded (it reads the page's own objects):
 *
 * - **WebGL**: `renderer.renderLists.get(scene, renderCallDepth)` → a
 *   `WebGLRenderList` with `opaque` / `transmissive` / `transparent` arrays.
 * - **WebGPU** (r18x): `renderer._renderLists.get(scene, camera)` → a
 *   `RenderList` (opaque / transparent; transmissive may be absent).
 *
 * Read AFTER the render populated the list — `get` returns the existing
 * populated list for a hit (it only creates an empty one on a miss, which
 * yields harmless zeros). Returns null when no bucket is readable.
 */
/** A non-empty string, else undefined — three leaves `name` as "" when unset. */
function firstName(value: unknown): string | undefined {
    return typeof value === "string" && value.length ? value : undefined;
}

/**
 * The name on a render target's texture. Multi-target renders (MRT) hold an
 * ARRAY of textures — take the first one that was actually named, since that is
 * what a person would call the target.
 */
function textureNameOf(record: Record<string, unknown>): string | undefined {
    const texture = record.texture;
    if (Array.isArray(texture)) {
        for (const entry of texture) {
            const name = firstName((entry as { name?: unknown } | null)?.name);
            if (name) return name;
        }
        return undefined;
    }
    return firstName((texture as { name?: unknown } | null | undefined)?.name);
}

export function readRenderListStats(renderer: unknown, scene: unknown, camera: unknown, depth: number): RenderListStats | null {
    try {
        const r = renderer as { renderLists?: { get?: (s: unknown, d: unknown) => unknown }; _renderLists?: { get?: (s: unknown, c: unknown) => unknown } };
        let list = r.renderLists?.get?.(scene, depth);              // WebGL: (scene, renderCallDepth)
        if (!list) list = r._renderLists?.get?.(scene, camera);     // WebGPU: (scene, camera)
        if (!list || typeof list !== "object") return null;
        const l = list as { opaque?: unknown; transmissive?: unknown; transparent?: unknown };
        const len = (v: unknown): number | undefined => (Array.isArray(v) ? v.length : undefined);
        const opaque = len(l.opaque);
        const transmissive = len(l.transmissive);
        const transparent = len(l.transparent);
        if (opaque === undefined && transmissive === undefined && transparent === undefined) return null;
        const o = opaque ?? 0, tm = transmissive ?? 0, tp = transparent ?? 0;
        return { opaque: o, transmissive: tm, transparent: tp, total: o + tm + tp };
    } catch {
        return null;
    }
}

// #region Capture

interface PatchedRenderer {
    renderer: RendererLike;
    originalRender: RendererLike["render"];
    /** three's WebGLShadowMap, when the renderer has one */
    shadowMap?: { render?: Function };
    /** the render call currently executing — a shadow pass belongs before it */
    currentCall?: CapturedRenderCall;
    /** set while shadowMap.render runs, until it binds a map (and so becomes a pass) */
    pendingShadowRender?: { scene: unknown, camera: unknown };
    originalShadowRender?: Function;
    wrappedShadowRender?: Function;
    wrappedRender: RendererLike["render"];
    originalSetRenderTarget: RendererLike["setRenderTarget"];
    wrappedSetRenderTarget: RendererLike["setRenderTarget"];
    /** Output of the next render call: a render target object, or null = canvas. */
    currentTarget: unknown;
    depth: number;
}

export class RenderCapture {
    private readonly frameCount: number;
    private readonly scheduleFrame: (callback: () => void) => void;
    private readonly renderParticipants?: () => readonly RenderParticipant[];

    private patched: PatchedRenderer[] = [];
    private resources = new Map<string, CapturedResource>();
    // WeakMap (NOT Map): the standing observer sees every scene/camera/target
    // ever rendered. A strong Map keyed by these objects pinned entire scene
    // graphs forever — on pages that create a fresh Scene per frame that grew
    // the JS heap without bound (multi-GB OOM). WeakMap lets dead objects go;
    // their `resources` snapshot entries are pruned separately below.
    private resourceIds = new WeakMap<object, string>();
    private calls: CapturedRenderCall[] = [];

    private active = false;
    private frameCounter = 0; // 0 = partial pre-heartbeat frame (discarded)
    /** Frames closed WITH render calls — idle heartbeat ticks don't count. */
    private completedFrames = 0;
    private order = 0;
    private generatedIds = 0;
    private resolve: ((result: RenderCaptureResult) => void) | null = null;

    constructor(options: RenderCaptureOptions = {}) {
        this.frameCount = options.frames === "continuous" ? Infinity : Math.max(1, options.frames ?? 1);
        this.onFrame = options.onFrame;
        this.renderParticipants = options.renderParticipants;
        this.scheduleFrame = options.scheduleFrame
            ?? ((callback) => globalThis.requestAnimationFrame(callback));
    }

    private readonly onFrame?: (frame: RenderCaptureResult) => void;

    /**
     * Patches additional renderers into a running capture/observation — used by
     * the standing observer as renderers are discovered over time.
     */
    addRenderers(renderers: readonly object[]): void {
        if (!this.active) return;
        for (const renderer of renderers) {
            if (!isRendererLike(renderer)) continue;
            if (this.patched.some(p => p.renderer === renderer)) continue;
            this.patch(renderer);
        }
    }

    get isCapturing(): boolean {
        return this.active;
    }

    /**
     * Patches the given renderers, records the next N full frames, unpatches,
     * and resolves with the recorded data. Renderers that don't look like a
     * renderer (no `render` function) are skipped.
     */
    capture(renderers: readonly object[]): Promise<RenderCaptureResult> {
        if (this.active) throw new Error("RenderCapture is already capturing");
        this.active = true;

        for (const renderer of renderers) {
            if (!isRendererLike(renderer)) continue;
            if (this.patched.some(p => p.renderer === renderer)) continue; // dedupe
            this.patch(renderer);
        }

        const promise = new Promise<RenderCaptureResult>((resolve) => {
            this.resolve = resolve;
        });

        // Heartbeat: the first tick opens frame 0; each later tick closes the
        // open frame ONLY if it recorded render calls. On-demand pages (orbit
        // controls, paused animations) tick the heartbeat without rendering —
        // an idle tick must neither finish the capture empty nor count toward
        // `frameCount`; the open frame simply stays armed until the page
        // renders again. After `frameCount` rendered frames the capture ends.
        const tick = () => {
            if (!this.active) return;
            if (this.frameCounter === 0) {
                this.frameCounter = 1; // open frame 0 (pre-heartbeat calls are discarded)
                this.scheduleFrame(tick);
                return;
            }
            const openIndex = this.frameCounter - 1;
            if (this.calls.some(c => c.frame === openIndex)) {
                if (this.frameCount === Infinity) {
                    // Standing observer: report the rendered frame, drain its
                    // calls, and keep observing on the same open index. Heavy
                    // objects are no longer retained (WeakMap resourceIds); the
                    // `resources` snapshot map still gains a small entry per
                    // distinct rendered object, so evict dead-WeakRef entries
                    // once it grows past a cap (per-frame-scene pages).
                    const frameCalls = this.calls
                        .filter(c => c.frame === openIndex)
                        .map(c => ({ ...c, frame: 0 }));
                    this.calls = this.calls.filter(c => c.frame !== openIndex);
                    this.pruneDeadResources();
                    this.onFrame?.({ frames: [{ index: 0, calls: frameCalls }], resources: this.resources, cancelled: false });
                } else {
                    this.completedFrames++;
                    if (this.completedFrames >= this.frameCount) {
                        this.finish(false);
                        return;
                    }
                    this.frameCounter++;
                }
            }
            this.scheduleFrame(tick);
        };
        this.scheduleFrame(tick);

        return promise;
    }

    /** Ends the capture early; the pending promise resolves with partial data. */
    cancel(): void {
        if (this.active) this.finish(true);
    }

    // #region Patching

    private patch(renderer: RendererLike): void {
        const entry: PatchedRenderer = {
            renderer,
            originalRender: renderer.render,
            wrappedRender: undefined!,
            originalSetRenderTarget: renderer.setRenderTarget,
            wrappedSetRenderTarget: undefined,
            currentTarget: this.safeGetRenderTarget(renderer),
            depth: 0,
        };

        const self = this;

        entry.wrappedRender = function (this: unknown, ...args: unknown[]): unknown {
            const callDepth = entry.depth;
            const restorations: ((completed: boolean) => void)[] = [];
            try {
                for (const participant of self.renderParticipants?.() ?? []) {
                    const restore = participant({
                        renderer: entry.renderer,
                        scene: args[0],
                        camera: args[1],
                        target: entry.currentTarget,
                        depth: callDepth,
                    });
                    if (restore) restorations.push(restore);
                }
            }
            catch (error) {
                for (let index = restorations.length - 1; index >= 0; index--) restorations[index](false);
                throw error;
            }
            const call = self.recordCall(entry, args[0], args[1]);
            const outerCall = entry.currentCall;
            if (call) entry.currentCall = call;
            entry.depth++;
            let completed = false;
            try {
                const result = entry.originalRender.apply(this, args);
                completed = true;
                return result;
            } finally {
                try {
                    entry.depth--;
                    entry.currentCall = outerCall;
                    // Read the sorted draw list AFTER the render populated it (it
                    // persists until the next render of this scene re-inits it) —
                    // counts only, cheap, best-effort.
                    if (call) {
                        const stats = readRenderListStats(entry.renderer, args[0], args[1], callDepth);
                        if (stats) (call as { renderList?: RenderListStats }).renderList = stats;
                    }
                }
                finally {
                    for (let index = restorations.length - 1; index >= 0; index--) restorations[index](completed);
                }
            }
        };
        renderer.render = entry.wrappedRender;

        // Shadow maps are drawn INSIDE render(), from renderer.shadowMap.render() —
        // never a render() call of their own, so a render()-only capture shows a
        // frame with no shadow work while it may be the frame's largest cost.
        const shadowMap = (renderer as { shadowMap?: { render?: Function } }).shadowMap;
        if (shadowMap && typeof shadowMap.render === "function") {
            entry.shadowMap = shadowMap;
            entry.originalShadowRender = shadowMap.render;
            entry.wrappedShadowRender = function (this: unknown, ...args: unknown[]): unknown {
                // A pass is recorded only once the shadow map is BOUND, because
                // three exits this function without drawing on most frames —
                // shadows off, nothing needing an update, no casting light
                // (WebGLShadowMap.js:74-77) — and recording on entry gave those
                // no-ops a pass node on whatever target was bound before them
                // (on an SSAO page, the AO buffer, image and all).
                // (lights, scene, camera) — the scene/camera the shadows are for
                entry.pendingShadowRender = { scene: args[1], camera: args[2] };
                entry.depth++;
                try { return entry.originalShadowRender!.apply(this, args); }
                finally { entry.depth--; entry.pendingShadowRender = undefined; }
            };
            shadowMap.render = entry.wrappedShadowRender;
        }

        if (typeof entry.originalSetRenderTarget === "function") {
            const originalSetRenderTarget = entry.originalSetRenderTarget;
            entry.wrappedSetRenderTarget = function (this: unknown, ...args: unknown[]): unknown {
                entry.currentTarget = args[0] ?? null;
                // the first target a shadow render binds IS the pass: recorded
                // here, so its output is that map rather than what preceded it
                const pending = entry.pendingShadowRender;
                if (pending && args[0] && typeof args[0] === "object") {
                    entry.pendingShadowRender = undefined;
                    self.recordCall(entry, pending.scene, pending.camera, "shadow");
                }
                return originalSetRenderTarget.apply(this, args);
            };
            renderer.setRenderTarget = entry.wrappedSetRenderTarget;
        }

        this.patched.push(entry);
    }

    private unpatchAll(): void {
        for (const entry of this.patched) {
            // Restore only if still ours — if the page replaced the method
            // mid-capture (hot reload, re-init) we must not clobber it.
            if (entry.renderer.render === entry.wrappedRender) {
                entry.renderer.render = entry.originalRender;
            }
            if (entry.wrappedSetRenderTarget
                && entry.renderer.setRenderTarget === entry.wrappedSetRenderTarget) {
                entry.renderer.setRenderTarget = entry.originalSetRenderTarget;
            }
            if (entry.shadowMap && entry.wrappedShadowRender
                && entry.shadowMap.render === entry.wrappedShadowRender) {
                entry.shadowMap.render = entry.originalShadowRender;
            }
        }
        this.patched = [];
    }

    private safeGetRenderTarget(renderer: RendererLike): unknown {
        try {
            return typeof renderer.getRenderTarget === "function"
                ? renderer.getRenderTarget() ?? null
                : null;
        } catch {
            return null;
        }
    }

    // #region Recording

    private recordCall(entry: PatchedRenderer, scene: unknown, camera: unknown, kind?: "shadow"): CapturedRenderCall | null {
        // frameCounter 0 = the partial frame before the first heartbeat tick.
        if (!this.active || this.frameCounter === 0) return null;
        // OUR OWN renders (previews, overlays, the thumbnail readbacks that fill
        // these very cards) are not the page's frame — and the GPU measurement
        // leaves them out too, so capturing them left the two unable to agree
        // about what ran.
        if (isEditorInternalRender(scene, camera)) return null;

        const call: CapturedRenderCall = {
            order: this.order++,
            frame: this.frameCounter - 1,
            renderer: this.resourceFor(entry.renderer, "renderer"),
            scene: this.resourceFor(scene, "scene"),
            camera: this.resourceFor(camera, "camera"),
            target: entry.currentTarget && typeof entry.currentTarget === "object"
                ? this.resourceFor(entry.currentTarget, "renderTarget")
                : this.canvasFor(entry),
            depth: entry.depth,
            ...(kind ? { kind } : {}),
        };
        // A shadow pass runs at the START of the render call that triggered it, so
        // it belongs BEFORE it — recorded after only because we see render() first.
        const parent = kind === "shadow" ? entry.currentCall : undefined;
        const parentIndex = parent ? this.calls.indexOf(parent) : -1;
        if (parent && parentIndex >= 0) {
            const swapped: CapturedRenderCall = { ...call, order: parent.order };
            (parent as { order: number }).order = call.order;
            this.calls.splice(parentIndex, 0, swapped);
            return swapped;
        }
        this.calls.push(call);
        return call;
    }

    /** The renderer's canvas as a pseudo-resource (output when no target is bound). */
    private canvasFor(entry: PatchedRenderer): string {
        const canvas = entry.renderer.domElement;
        const key = canvas && typeof canvas === "object" ? canvas : entry.renderer;
        const existing = this.resourceIds.get(key);
        if (existing && this.resources.get(existing)?.kind === "canvas") return existing;
        return this.registerResource(key, "canvas");
    }

    private resourceFor(value: unknown, kind: CapturedResourceKind): string {
        const obj = value && typeof value === "object" ? (value as object) : null;
        if (!obj) return this.registerResource({ placeholder: kind }, kind);
        return this.resourceIds.get(obj) ?? this.registerResource(obj, kind);
    }

    private registerResource(obj: object, kind: CapturedResourceKind): string {
        const record = obj as Record<string, unknown>;
        const uuid = typeof record.uuid === "string" ? record.uuid : undefined;
        const id = uuid ?? `${kind}#${this.generatedIds++}`;

        if (!this.resources.has(id)) {
            const ref = new WeakRef(obj);
            this.resources.set(id, {
                id,
                kind,
                type: typeof record.type === "string" ? record.type : undefined,
                // A WebGLRenderTarget carries no name of its own — three names the
                // TEXTURE ("PointShadowMap", "reflection"), and that is the only
                // human label a target ever has. Falling back to it is what keeps
                // the graph from reading as a row of identical "Target WxH" cards.
                name: firstName(record.name) ?? firstName(textureNameOf(record)),
                width: typeof record.width === "number" ? record.width : undefined,
                height: typeof record.height === "number" ? record.height : undefined,
                deref: () => ref.deref(),
            });
        }
        this.resourceIds.set(obj, id);
        return id;
    }

    /** Cap past which {@link pruneDeadResources} sweeps the snapshot map. */
    private static readonly RESOURCE_PRUNE_THRESHOLD = 2048;

    /**
     * Evicts `resources` snapshot entries whose object has been collected —
     * keeps the map bounded on standing (continuous) observation without
     * pinning anything (entries hold only a WeakRef). Only sweeps once the map
     * grows past a cap, so the per-frame cost is amortised to ~nothing.
     */
    private pruneDeadResources(): void {
        if (this.resources.size < RenderCapture.RESOURCE_PRUNE_THRESHOLD) return;
        for (const [id, res] of this.resources) {
            if (!res.deref()) this.resources.delete(id);
        }
    }

    // #region Finish

    private finish(cancelled: boolean): void {
        this.active = false;
        this.unpatchAll();

        // Closed frames all contained render calls by construction (idle ticks
        // never close a frame). On cancel, keep the still-open frame when it
        // recorded something — partial data beats none.
        const openIndex = this.frameCounter - 1;
        const openHasCalls = openIndex >= 0 && this.calls.some(c => c.frame === openIndex);
        const completedFrames = cancelled
            ? this.completedFrames + (openHasCalls ? 1 : 0)
            : this.completedFrames;
        const frames: CapturedFrame[] = [];
        for (let index = 0; index < completedFrames; index++) {
            frames.push({ index, calls: this.calls.filter(c => c.frame === index) });
        }

        const resolve = this.resolve;
        this.resolve = null;
        resolve?.({ frames, resources: this.resources, cancelled });
    }
}
